/**
 * Notification Bell Component
 * 
 * Displays a bell icon with unread notification count badge.
 * Redirects to communications center when clicked.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { initializeApiClient } from 'utils/apiClient';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useSignalR } from 'hooks/useSignalR';
import logger from 'utils/logger';

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  
  const { user } = useAuth();
  // Always call useMsal hook, but handle the case when MSAL is not available
  let instance = null;
  try {
    const msalData = useMsal();
    instance = msalData.instance;
  } catch (error) {
    // MSAL provider is not available (authentication disabled)
    instance = null;
  }
  const navigate = useNavigate();


  // Handle notification count updates from SignalR
  // SignalR sends: { type: 'notificationCount', unreadCount: number, countByType: {...} }
  // SignalR provides the initial count when connecting, so no REST API call needed
  const handleNotificationCountUpdate = useCallback((message) => {
    if (message && (message.type === 'notificationCount' || message.unreadCount !== undefined)) {
      const count = message.unreadCount || 0;
      logger.debug('NOTIFICATION-BELL', 'Received notification count update via SignalR', { count });
      setUnreadCount(count);
    }
  }, []);

  // Use SignalR hook for real-time updates
  const userId = user?.id || user?.entra_object_id || null;
  const {
    connect,
    disconnect
  } = useSignalR(userId, null, handleNotificationCountUpdate, null);

  // Initialize API client and connect to SignalR for real-time updates
  useEffect(() => {
    // Initialize API client
    if (instance) {
      initializeApiClient(instance);
    }
    
    // Don't set up connection if no user
    if (!user) {
      setUnreadCount(0);
      disconnect();
      return;
    }
    
    // Connect to SignalR - it will handle all real-time updates
    // Initial count will be received via SignalR once connected
    connect();
    
    return () => {
      disconnect();
    };
  }, [user, instance, connect, disconnect]);

  // SignalR will provide the initial count when it connects
  // No REST API calls needed - rely entirely on SignalR for updates

  const handleBellClick = () => {
    navigate('/communications');
  };


  if (!user) {
    return null;
  }

  return (
    <div className="notification-bell-container">
      <span 
        className="notification-bell-text"
        onClick={handleBellClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleBellClick();
          }
        }}
        aria-label="Chat and Calendar"
        title={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Chat and Calendar'}
      >
        Chat and Calendar
        {unreadCount > 0 && (
          <span className="notification-count-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </span>
    </div>
  );
};

export default NotificationBell;

